/*
 * 登录页。
 *
 * 按宿主给出**当前真的能用**的方式，不摆不可用的入口：
 *
 *   普通浏览器   只有手机号 + 验证码。不显示「微信登录」——
 *                旧项目的做法是显示了但点了没反应。
 *   微信内 H5    服务号一键授权为主，手机号为备选。
 *   小程序壳内   壳已经把 code 传进来了，正常情况下走不到这一页；
 *                走到了说明 code 过期或被用过，给一条明确的出路。
 *
 * 服务端下发的 `loginMethods` 与宿主能力求交集才是最终可用集合：
 * 服务端没配微信凭据时，即使在微信里也不该显示微信按钮。
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DreamBackdrop } from "../../components/DreamStage";
import { Button } from "../../components/ui";
import { ApiError, describeFailure } from "../../services/api-client";
import {
  loginAsUser,
  loginByPhone,
  requestSmsCode,
  wechatOfficialLoginUrl,
} from "../../services/auth-api";
import { requestMiniappLogin } from "../../services/host-bridge";
import { LOCAL_ASSETS } from "../../services/offline-preview";
import { useAppearance, useSession } from "../../state/session";
import "./auth.css";

/** 与后端 `CHINA_MOBILE` 一致。前端先拦一道只是为了少一次往返，不是安全边界。 */
const PHONE = /^1[3-9]\d{9}$/;
const CODE = /^\d{6}$/;

/** 服务号回调失败时挂在地址上的原因。不含任何细节，详情在服务端日志里。 */
const WECHAT_ERRORS: Record<string, string> = {
  wechat_state: "微信授权凭据校验失败，请重新试一次。",
  wechat_failed: "微信授权没能完成，可以改用手机号登录。",
  wechat_unavailable: "服务端还没有配置微信登录，请先用手机号登录。",
};

export function LoginPage() {
  const session = useSession();
  const appearance = useAppearance();
  const navigate = useNavigate();
  const location = useLocation();

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const codeInput = useRef<HTMLInputElement>(null);

  // 登录成功后由 SessionProvider 把 status 切成 authenticated，这里负责跳走。
  // 判断放在 effect 里而不是渲染期：渲染期 navigate 会触发 React 的警告。
  //
  // 普通登录回首页，不读任意 `?redirect=`。只有资料深链接保留同站资料目标。
  // 触发登录最多的场景是用户自己点了
  // 「退出登录」，那时他停在设置页或资料页；送回那里等于让人重新登录之后
  // 第一眼看到的是自己上次关掉的抽屉，而不是缈缈。见 `App.tsx` 的 RequireSession。
  const authenticated = session.status === "authenticated" || session.status === "needs_profile";
  const informationReturnTo: unknown = (location.state as { informationReturnTo?: unknown } | null)?.informationReturnTo;
  const loginTarget = typeof informationReturnTo === "string" &&
    /^\/information(?:\/[0-9a-f-]{36})?(?:\?[^#\\]*)?$/i.test(informationReturnTo)
    ? informationReturnTo : "/";
  useEffect(() => {
    if (!authenticated) return;
    navigate(loginTarget, { replace: true });
  }, [authenticated, navigate, loginTarget]);

  // 服务号回调失败时会带 ?error= 回来。读完就从地址栏抹掉，
  // 否则刷新一次又弹一遍同样的错。
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reason = params.get("error");
    if (!reason) return;
    setFailure(WECHAT_ERRORS[reason] ?? "登录没能完成，请重试。");
    params.delete("error");
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const sendCode = useCallback(async () => {
    if (!PHONE.test(phone)) {
      setFailure("请输入 11 位手机号");
      return;
    }
    setSending(true);
    setFailure(null);
    try {
      const result = await requestSmsCode(phone);
      if (result.environment === "development") {
        /*
         * 开发免密环境。
         *
         * 这里曾经还会点亮一条开发环境橙色横幅，
         * 现已去掉：那是给开发者看的部署状态，不该出现在用户界面上。
         * 下面这句 notice 已经把「任意 6 位数字都能登录」说清楚了，
         * 而且只在真的点了获取验证码之后才出现，不占首屏。
         *
         * 真正的防线也不在界面：线上模式根本不存在免密分支。
         */
        setCode(result.devCode ?? "000000");
        setNotice("开发环境：任意 6 位数字都能登录，已自动填好");
      } else {
        setNotice("验证码已发送");
        setCooldown(result.cooldownSeconds || 60);
      }
      codeInput.current?.focus();
    } catch (cause) {
      setFailure(explain(cause));
      // 后端限流会带上 retryAfterSeconds，用它接管倒计时，
      // 而不是让用户每秒点一次去撞同一堵墙。
      if (cause instanceof ApiError && cause.retryAfterSeconds) {
        setCooldown(cause.retryAfterSeconds);
      }
    } finally {
      setSending(false);
    }
  }, [phone]);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!PHONE.test(phone)) {
        setFailure("请输入 11 位手机号");
        return;
      }
      if (!CODE.test(code)) {
        setFailure("验证码是 6 位数字");
        return;
      }

      setNotice(null);
      setFailure(null);
      session.beginLogin();
      try {
        session.adopt(await loginByPhone(phone, code));
      } catch (cause) {
        session.cancelLogin();
        setFailure(explain(cause));
      }
    },
    [phone, code, session],
  );

  const host = session.host;
  const submitting = session.status === "authenticating";

  // 服务端支持 × 宿主可用 = 实际可用。任一为否都不显示按钮。
  const showWechatOfficial =
    session.loginMethods.wechatOfficial && host.kind === "wechat-h5";
  const showMiniappHint = host.kind === "miniapp-webview";

  return (
    <div className={`login-page login-page--${host.formFactor}`}>
      <DreamBackdrop
        url={appearance.backgroundUrl}
        fallbackUrl={LOCAL_ASSETS.background}
      />

      <main className="login-card glass rise-in">
        <header className="login-card__head">
          {appearance.avatarUrl ? (
            <img className="login-card__avatar" src={appearance.avatarUrl} alt="" />
          ) : null}
          <h1 className="login-card__title">入梦</h1>
          <p className="login-card__subtitle">
            {appearance.fromPreview
              ? "还没连上灵境，先登录看看"
              : `${appearance.name}在梦境入口等你`}
          </p>
        </header>

        {showWechatOfficial ? (
          <>
            <a className="btn btn--primary btn--block" href={wechatOfficialLoginUrl(RETURN_TO)}>
              微信一键登录
            </a>
            <p className="login-divider">
              <span>或用手机号</span>
            </p>
          </>
        ) : null}

        {showMiniappHint ? <MiniappNotice search={location.search} /> : null}

        <form className="login-form" onSubmit={submit} noValidate>
          <label className="login-field">
            <span className="login-field__label">手机号</span>
            <input
              className="field"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={11}
              placeholder="11 位手机号"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              disabled={submitting}
            />
          </label>

          <label className="login-field">
            <span className="login-field__label">验证码</span>
            <div className="login-field__row">
              <input
                ref={codeInput}
                className="field"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="6 位验证码"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                disabled={submitting}
              />
              <Button
                tone="ghost"
                onClick={() => void sendCode()}
                disabled={sending || submitting || cooldown > 0 || !PHONE.test(phone)}
              >
                {cooldown > 0 ? `${cooldown}s` : sending ? "发送中" : "获取验证码"}
              </Button>
            </div>
          </label>

          {notice ? (
            <p className="login-alert login-alert--info" role="status">
              {notice}
            </p>
          ) : null}
          {failure ? (
            <p className="login-alert login-alert--error" role="alert">
              {failure}
            </p>
          ) : null}

          <Button
            tone="primary"
            block
            type="submit"
            disabled={submitting || !PHONE.test(phone) || !CODE.test(code)}
          >
            {submitting ? "正在入梦" : "入梦"}
          </Button>
        </form>

        <p className="login-foot">
          未注册的手机号将自动创建账号。缈缈是 AI 角色，对话由模型生成。
        </p>

        <p className="login-icp">
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">
            浙ICP备2022025027号-6
          </a>
        </p>

        {session.loginMethods.devImpersonation ? <DevLoginPanel /> : null}
      </main>
    </div>
  );
}

/**
 * 开发期冒充登录。
 *
 * 渲染条件是服务端下发的 `loginMethods.devImpersonation`，它已经把那条路由的
 * 两道闸（development + 请求来自服务端本机）算过一遍了，
 * 所以**画出来的入口一定点得动**。
 *
 * ⚠ 别只看 `environment`：development 对远程访问也为真。
 * 用它当判据会在一台开发服务器上，给远程访问者画一个点了 403
 * 的框——摆一个点不动的入口比不摆更糟。
 * ⚠ 也别在这里读构建模式：服务端下发的环境才是登录行为的权威来源。
 *
 * 为什么需要它：库里 98% 的账号只有微信 openid 没有手机号，短信那条路
 * 登不进他们；而输一个没注册的手机号会**新建**账号，看起来登进去了，
 * 其实是个空号。这条路查不到就报错，绝不建号。
 *
 * 折叠起来是因为它对「登录」这件事毫无贡献，只是恰好长在这一页上：
 * 唯一用得到它的时刻，是你手里已经攥着一个从库里抄来的 userId。
 */
function DevLoginPanel() {
  const session = useSession();
  const [userId, setUserId] = useState("019fac8e-52d9-70ad-afc2-198d11669dc7");
  const [failure, setFailure] = useState<string | null>(null);

  const submitting = session.status === "authenticating";
  const ready = userId.trim().length > 0;

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const id = userId.trim();
      if (!id) return;

      setFailure(null);
      session.beginLogin();
      try {
        session.adopt(await loginAsUser(id));
      } catch (cause) {
        session.cancelLogin();
        setFailure(explain(cause));
      }
    },
    [userId, session],
  );

  return (
    <details className="login-dev">
      <summary className="login-dev__summary">开发登录</summary>
      <form className="login-dev__body" onSubmit={submit} noValidate>
        <p className="login-dev__hint">
          按 userId 直接进入某个账号，不创建新账号。候选 ID 用{" "}
          <code>tools/list-test-users.ts</code> 列。
        </p>
        <div className="login-field__row">
          <input
            className="field"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="userId（uuid）"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={submitting}
          />
          <Button tone="ghost" type="submit" disabled={submitting || !ready}>
            {submitting ? "进入中" : "进入"}
          </Button>
        </div>
        {failure ? (
          <p className="login-alert login-alert--error" role="alert">
            {failure}
          </p>
        ) : null}
      </form>
    </details>
  );
}

/**
 * 小程序壳内的提示。
 *
 * 正常路径下壳会在打开 web-view 前就把 `wx.login` 的 code 拼进地址，
 * 用户根本看不到这一页。看到了说明那个 code 已经过期或被用过——
 * code 是一次性的，刷新页面并不会重新生成。
 */
function MiniappNotice({ search }: { search: string }) {
  const [tried, setTried] = useState(false);

  return (
    <div className="login-alert login-alert--info">
      <strong>微信身份需要小程序重新授权</strong>
      登录凭据由小程序生成，刷新本页不会重新获取。
      <Button
        tone="quiet"
        onClick={() => {
          void requestMiniappLogin(RETURN_TO).then((started) => {
            if (!started) setTried(true);
          });
        }}
      >
        请小程序重新授权
      </Button>
      {tried ? <span>当前壳没有开放跳转能力，请退出小程序后重新进入。</span> : null}
    </div>
  );
}

/**
 * 微信 / 小程序授权完成后送用户回哪里。
 *
 * 这与「登录后跳哪一页」是同一个答案：**首页**。它之所以还需要显式传给
 * 服务端与小程序壳，是因为那两条路径要离开本页再回来，回来的地址得写死在
 * 授权请求里——不是可选项。
 *
 * 曾经这里读 `?redirect=`，已删。理由见 `App.tsx` 的 RequireSession。
 */
const RETURN_TO = "/";

/**
 * 把失败翻译成用户能读的一句话。
 *
 * 服务端对「没发过码」「过期」「填错了」返回同一个 `BAD_CODE`——
 * 区分它们等于告诉爆破方「这个号有在途验证码」。这里也不替它编细节。
 */
function explain(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.kind === "http" || cause.kind === "unauthorized" || cause.kind === "forbidden") {
      return cause.message;
    }
    return describeFailure(cause).detail;
  }
  return cause instanceof Error ? cause.message : String(cause);
}
